import React, { useState, useEffect, useCallback, useContext, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import * as emailApi from '../../services/emailMarketingApi';
import CampaignModal from './CampaignModal';
import CreateCampaignModal from './CreateCampaignModal';

import {
  Mail,
  Send,
  Eye,
  MousePointer,
  Plus,
  RefreshCw,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Clock,
  TrendingUp,
  RotateCcw,
  Info,
} from 'lucide-react';

const DashboardLayout = lazy(() => import('../DashboardLayout'));
const NavigationMenu  = lazy(() => import('../NavigationMenu'));

const EmailMarketingDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useContext(AuthContext);

  const userRole    = user?.role || 'campaignreport';
  const isAdmin     = ['admin', 'superadmin', 'campaignadmin'].includes(userRole);
  const isReportOnly = userRole === 'campaignreport' || userRole === 'pdcare';

  const [activeTab,       setActiveTab]       = useState(isReportOnly ? 'campaigns' : 'dashboard');
  const [stats,           setStats]           = useState(null);
  const [campaigns,       setCampaigns]       = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [filters,         setFilters]         = useState({ status: '', search: '', page: 1 });
  const [pagination,      setPagination]      = useState({
    current_page: 1,
    last_page:    1,
    total:        0,
    per_page:     15,
  });

  // ── Auth guard ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  // ── Listen for force-logout events ────────────────────────────────────────
  useEffect(() => {
    const handleForceLogout = () => navigate('/login');
    window.addEventListener('forceLogout', handleForceLogout);
    return () => window.removeEventListener('forceLogout', handleForceLogout);
  }, [navigate]);

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      if (isAdmin) {
        const statsResponse = await emailApi.getStats();
        setStats(statsResponse.data);
      }
      const campaignsResponse = await emailApi.getCampaigns(filters.page, {
        status:   filters.status,
        search:   filters.search,
      });
      setCampaigns(campaignsResponse.data.data || []);
      setPagination({
        current_page: campaignsResponse.data.current_page,
        last_page:    campaignsResponse.data.last_page,
        total:        campaignsResponse.data.total,
        per_page:     campaignsResponse.data.per_page,
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filters.page, filters.status, filters.search, isAuthenticated, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleCreateCampaign = () => setShowCreateModal(true);

  const handleCampaignCreated = () => {
    setShowCreateModal(false);
    setActiveTab('campaigns');
    loadData();
  };

  /**
   * Single API call → backend/cron handles the rest.
   * No while-loop, no stopRef, no chunk tracking here.
   */
  const handleSendCampaign = async (campaignId) => {
    if (!isAdmin) return;

    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) return;

    const total     = campaign.total_recipients || 0;
    const remaining = Math.max(0, total - (campaign.total_processed || 0));
    const minutes   = Math.max(1, Math.ceil(remaining / 25));

    const confirmMsg = campaign.status === 'processing' && (campaign.total_processed || 0) > 0
      ? `Resume sending?\n\n${campaign.total_processed} already sent, ${remaining} remaining (~${minutes} min).\n\nSending runs on the server — you can close the browser.`
      : `Send to ${total || '?'} recipients?\n\nEstimated time: ~${minutes} min.\nSending runs on the server — you can close the browser.`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await emailApi.sendCampaign(campaignId);
      // Refresh list so status flips to 'processing'
      await loadData();
      // Open the modal so they can watch live progress
      const refreshed = campaigns.find(c => c.id === campaignId);
      setSelectedCampaign(refreshed || campaign);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to start campaign.');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await emailApi.deleteCampaign(campaignId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to delete campaign');
    }
  };

  // ── Sub-components ─────────────────────────────────────────────────────────
  const StatCard = ({ label, value, icon: Icon, color, trend }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value?.toLocaleString() || '0'}</p>
          {trend && (
            <p className={`text-xs mt-2 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '+' : ''}{trend}% from last month
            </p>
          )}
        </div>
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <Icon size={20} color={color} />
        </div>
      </div>
    </div>
  );

  const StatusBadge = ({ status }) => {
    const styles = {
      draft:      'bg-gray-100 text-gray-800 border-gray-200',
      processing: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      completed:  'bg-green-100 text-green-800 border-green-200',
      failed:     'bg-red-100 text-red-800 border-red-200',
    };
    const icons = {
      draft:      Clock,
      processing: RefreshCw,
      completed:  CheckCircle,
      failed:     XCircle,
    };
    const Icon = icons[status] || AlertCircle;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.draft}`}>
        <Icon size={12} className={status === 'processing' ? 'animate-spin' : ''} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // ── Dashboard tab ──────────────────────────────────────────────────────────
  const renderDashboard = () => (
    <>
      <div className="mb-6 bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-6 text-white">
        <h2 className="text-xl font-semibold mb-2">Welcome back, {user?.name || 'User'}!</h2>
        <p className="text-blue-100">Here's what's happening with your email campaigns today.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total Campaigns" value={stats?.total.campaigns}    icon={Mail}         color="#2563eb" />
        <StatCard label="Emails Sent"     value={stats?.total.sent}         icon={Send}         color="#059669" />
        <StatCard label="Unique Opens"    value={stats?.total.unique_opens} icon={Eye}          color="#7c3aed" />
        <StatCard label="Unique Clicks"   value={stats?.total.unique_clicks}icon={MousePointer} color="#ea580c" />
        <StatCard
          label="Open Rate"
          value={stats?.total.sent
            ? ((stats.total.unique_opens / stats.total.sent) * 100).toFixed(1) + '%'
            : '0%'}
          icon={TrendingUp}
          color="#0891b2"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Latest Campaign */}
        {stats?.latest && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Latest Campaign</h3>
              <button
                onClick={() => setSelectedCampaign(stats.latest)}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                View Details <ChevronRight size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Subject</p>
                <p className="text-base font-medium text-gray-900">{stats.latest.subject}</p>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <StatusBadge status={stats.latest.status} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sent</p>
                  <p className="text-base font-medium text-gray-900">
                    {stats.latest.total_success}/{stats.latest.total_processed}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="text-sm text-gray-900">
                    {new Date(stats.latest.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          {stats?.recent?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                  {activity.status === 'success'
                    ? <CheckCircle size={16} className="text-green-500 mt-0.5" />
                    : <XCircle    size={16} className="text-red-500 mt-0.5" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{activity.email}</p>
                    <p className="text-xs text-gray-500">{activity.campaign_subject}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><Eye size={12} /> {activity.opens}</span>
                      <span className="flex items-center gap-1"><MousePointer size={12} /> {activity.clicks}</span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {new Date(activity.sent_at).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
          )}
        </div>
      </div>
    </>
  );

  // ── Campaigns tab ──────────────────────────────────────────────────────────
  const renderCampaigns = () => (
    <>
      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-initial">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={filters.search}
                onChange={e => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
              />
            </div>
            <select
              value={filters.status}
              onChange={e => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            <button
              onClick={loadData}
              title="Refresh"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {isAdmin && (
            <button
              onClick={handleCreateCampaign}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              New Campaign
            </button>
          )}
        </div>
      </div>

      {/* Info banner */}
      {isAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">
            <span className="font-medium">Background Sending:</span> Campaigns run entirely on the server
            (25 emails/min). You can close the browser at any time — sending continues automatically.
          </p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Processed</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Success</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Failed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {campaigns.length > 0 ? (
                campaigns.map(campaign => {
                  const isProcessing  = campaign.status === 'processing';
                  const remaining     = Math.max(0, (campaign.total_recipients || 0) - (campaign.total_processed || 0));
                  const estMinutes    = Math.max(1, Math.ceil(remaining / 25));
                  const canResume     = isProcessing && (campaign.total_processed || 0) > 0;
                  const pct           = campaign.total_recipients > 0
                    ? Math.round((campaign.total_processed / campaign.total_recipients) * 100)
                    : 0;

                  return (
                    <tr key={campaign.id} className={`hover:bg-gray-50 transition-colors ${isProcessing ? 'bg-yellow-50/40' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-xs text-gray-500">{campaign.code}</code>
                      </td>

                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedCampaign(campaign)}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline text-left"
                        >
                          {campaign.subject}
                        </button>
                        {/* Inline progress bar for processing campaigns */}
                        {isProcessing && campaign.total_recipients > 0 && (
                          <div className="mt-1.5">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-yellow-500 h-1.5 rounded-full transition-all duration-500"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <p className="text-xs text-yellow-700 mt-0.5">
                              {campaign.total_processed}/{campaign.total_recipients} ({pct}%) — ~{estMinutes} min left
                            </p>
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={campaign.status} />
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                        {(campaign.total_processed || 0).toLocaleString()}
                        {campaign.total_recipients > 0 && (
                          <span className="text-xs text-gray-400 ml-1">
                            / {campaign.total_recipients.toLocaleString()}
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-green-600 font-medium">
                        {(campaign.total_success || 0).toLocaleString()}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-red-600">
                        {(campaign.total_failure || 0).toLocaleString()}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(campaign.created_at).toLocaleDateString()}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* View */}
                          <button
                            onClick={() => setSelectedCampaign(campaign)}
                            title="View Details"
                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          >
                            <Eye size={17} />
                          </button>

                          {isAdmin && (
                            <>
                              {/* Send / Resume — only for draft, processing, failed */}
                              {['draft', 'processing', 'failed'].includes(campaign.status) && (
                                <button
                                  onClick={() => handleSendCampaign(campaign.id)}
                                  title={canResume ? 'Resume Campaign' : 'Send Campaign'}
                                  className={`p-1.5 rounded flex items-center gap-1 ${
                                    canResume
                                      ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'
                                      : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                                  }`}
                                >
                                  {canResume
                                    ? <><RotateCcw size={17} /><span className="text-xs">Resume</span></>
                                    : <><Send size={17} /><span className="text-xs">Send</span></>}
                                </button>
                              )}

                              {/* Delete — draft only */}
                              {campaign.status === 'draft' && (
                                <button
                                  onClick={() => handleDeleteCampaign(campaign.id)}
                                  title="Delete"
                                  className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                >
                                  <XCircle size={17} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No campaigns found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing{' '}
              {(pagination.current_page - 1) * pagination.per_page + 1} to{' '}
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
              {pagination.total} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page === pagination.last_page}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  // ── Loading / Error states ─────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (loading) {
    return (
      <Suspense fallback={<div className="text-center p-4">Loading…</div>}>
        <DashboardLayout title="Email Marketing" menu={<NavigationMenu />}>
          <div className="flex justify-center items-center h-screen">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mb-4" />
              <p className="text-lg text-gray-600">Loading email marketing dashboard…</p>
            </div>
          </div>
        </DashboardLayout>
      </Suspense>
    );
  }

  if (error) {
    return (
      <Suspense fallback={<div className="text-center p-4">Loading…</div>}>
        <DashboardLayout title="Email Marketing" menu={<NavigationMenu />}>
          <div className="flex justify-center items-center h-screen">
            <div className="bg-gradient-to-r from-red-100 to-red-50 border-l-4 border-red-500 p-6 rounded-lg shadow-lg max-w-md w-full">
              <div className="flex items-center">
                <AlertCircle className="text-red-500 text-2xl mr-3" />
                <div>
                  <h3 className="font-bold text-red-800 text-lg">Error</h3>
                  <p className="text-red-600 mt-1">{error}</p>
                  <button
                    onClick={loadData}
                    className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </DashboardLayout>
      </Suspense>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <Suspense fallback={<div className="text-center p-4">Loading…</div>}>
      <DashboardLayout title="Email Marketing Dashboard" menu={<NavigationMenu />}>
        <div className="container mx-auto px-4 py-8">

          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Email Marketing</h1>
              <p className="text-sm text-gray-500 mt-1">
                {isReportOnly
                  ? 'View and monitor email campaigns'
                  : 'Manage and send email campaigns to your customers'}
              </p>
            </div>

            {isAdmin && (
              <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'dashboard' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setActiveTab('campaigns')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'campaigns' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Campaigns
                </button>
              </div>
            )}
          </div>

          {/* Content */}
          {isReportOnly ? (
            renderCampaigns()
          ) : (
            <>
              {activeTab === 'dashboard' && renderDashboard()}
              {activeTab === 'campaigns' && renderCampaigns()}
            </>
          )}

          {/* Modals */}
          {selectedCampaign && (
            <CampaignModal
              campaign={selectedCampaign}
              onClose={() => { setSelectedCampaign(null); loadData(); }}
              onSendComplete={() => loadData()}
              isAdmin={isAdmin}
            />
          )}

          {showCreateModal && isAdmin && (
            <CreateCampaignModal
              onClose={() => setShowCreateModal(false)}
              onSuccess={handleCampaignCreated}
            />
          )}
        </div>
      </DashboardLayout>
    </Suspense>
  );
};

export default EmailMarketingDashboard;